import { Send } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import { useBatchExpiry } from "../../hooks/useBatchExpiry";
import type { PayoutBatchDetail } from "../../types/payoutBatches.types";

export interface BatchReviewFooterProps {
  batch: PayoutBatchDetail;
  isSyncing: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
}

type BlockReason = "none" | "syncing" | "expired" | "insufficient" | "blockers" | "no_permission" | null;

const blockReason = (
  batch: PayoutBatchDetail,
  isSyncing: boolean,
  isExpired: boolean,
  canSubmit: boolean,
): BlockReason => {
  if (isExpired) return "expired";
  if (!canSubmit) return "no_permission";
  if (isSyncing) return "syncing";
  if (batch.counters.selected === 0) return "none";
  if (batch.summary.wallet && !batch.summary.wallet.sufficient) return "insufficient";
  if (batch.summary.blockers.length > 0 || !batch.summary.can_submit) return "blockers";
  return null;
};

const REASON_KEYS = {
  none: "footer_blocked_none",
  syncing: "footer_blocked_syncing",
  expired: "footer_blocked_expired",
  insufficient: "footer_blocked_insufficient",
  blockers: "footer_blocked_blockers",
  no_permission: "footer_blocked_no_write",
} as const;

/**
 * The sticky ticket at the bottom of the review: what is about to be paid and the one way forward, which is
 * handing it to a second user. Disabled while a selection PATCH is in flight, because the fingerprint the
 * server would receive is not yet the one that describes what is on screen.
 */
export function BatchReviewFooter({ batch, isSyncing, canSubmit, onSubmit }: BatchReviewFooterProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const { isExpired } = useBatchExpiry(batch.expires_at);
  const reason = blockReason(batch, isSyncing, isExpired, canSubmit);
  const source = batch.source_currency;

  return (
    <div className="sticky bottom-0 z-10 -mx-2 border-t border-border bg-card/95 px-4 py-3 shadow-[var(--shadow-elev)] backdrop-blur supports-[backdrop-filter]:bg-card/80 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-sm text-foreground tabular-nums">
            {t("footer_summary", {
              count: batch.counters.selected,
              debit: `${formatCurrencyAmount(batch.totals.debit_indicative_selected, source, activeLanguage)} ${source}`,
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {reason ? t(REASON_KEYS[reason]) : t("footer_submit_hint")}
          </p>
        </div>
        <ColocaButton variant="primary" icon={Send} disabled={reason !== null} onClick={onSubmit}>
          {t("footer_submit")}
        </ColocaButton>
      </div>
    </div>
  );
}
